import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { ArrowUp, ArrowDown, Pencil, Trash2 } from "lucide-react";


import {
  fetchSpeakers,
  createSpeaker,
  updateSpeaker,
  deleteSpeaker,
  uploadSpeakerPhoto,
  deleteSpeakerPhoto
} from "../services/speakers.service"

/*
  SPEAKERS ADMIN PAGE
  ------------------
  Features:
  - List speakers
  - Count total speakers
  - Add speaker (modal)
  - Edit speaker (modal)
  - Change status (draft / confirmed)
  - Upload speaker photo
  - Delete speaker + cleanup photo
*/

const EMPTY_FORM = {
  full_name: "",
  organization: "",
  speaking_topic: "",
  speaking_type: "Keynote",
  status: "draft",
  bio: "",
  photo_url: ""
}

export default function Speakers() {
  const [speakers, setSpeakers] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [loading, setLoading] = useState(false)

  const [form, setForm] = useState(EMPTY_FORM)
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState("")

  /* ================= LOAD ================= */
  useEffect(() => {
    loadSpeakers()
  }, [])

  async function loadSpeakers() {
    const { data } = await fetchSpeakers()
    setSpeakers(data || [])
  }

//Move speaker up or down in the list
  async function moveSpeaker(speaker, direction) {
  const newOrder =
    direction === "up"
      ? speaker.display_order - 1
      : speaker.display_order + 1;

  await updateSpeaker(speaker.id, {
    display_order: newOrder
  });

  loadSpeakers();
}



  /* ================= MODAL ================= */
  function openAdd() {
    setEditing(null)
    setForm(EMPTY_FORM)
    setPhotoFile(null)
    setPhotoPreview("")
    setShowModal(true)
  }

  function openEdit(speaker) {
    setEditing(speaker)
    setForm({ ...speaker })
    setPhotoPreview(speaker.photo_url || "")
    setPhotoFile(null)
    setShowModal(true)
  }

  function closeModal() {
    setShowModal(false)
    setEditing(null)
    setForm(EMPTY_FORM)
    setPhotoFile(null)
    setPhotoPreview("")
  }

  /* ================= FORM ================= */
  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading) return

    setLoading(true)

    let photoUrl = form.photo_url

    // Upload new photo if selected
    if (photoFile) {
      try {
        photoUrl = await uploadSpeakerPhoto(photoFile)
      } catch (err) {
        toast.error(err.message || "Photo upload failed")
        setLoading(false)
        return
      }
    }

    const payload = { ...form, photo_url: photoUrl }

    const action = editing
      ? updateSpeaker(editing.id, payload)
      : createSpeaker(payload)

    const { error } = await action

    if (error) {
      toast.error(error.message)
      setLoading(false)
      return
    }

    toast.success(editing ? "Speaker updated" : "Speaker added")
    closeModal()
    loadSpeakers()
    setLoading(false)
  }

  /* ================= DELETE ================= */
  async function handleDelete(speaker) {
    const ok = window.confirm("Delete this speaker permanently?")
    if (!ok) return

    // Cleanup photo from storage
    if (speaker.photo_url) {
      try {
        await deleteSpeakerPhoto(speaker.photo_url)
      } catch (err) {
        console.warn("Photo cleanup failed", err)
      }
    }

    const { error } = await deleteSpeaker(speaker.id)
    if (error) {
      toast.error(error.message)
      return
    }

    toast.success("Speaker deleted")
    loadSpeakers()
  }

  /* ================= UI ================= */
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold text-slate-900">
          Speakers ({speakers.length})
        </h1>

        <button
          onClick={openAdd}
          className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-4 py-2 rounded-md"
        >
          Add Speaker
        </button>
      </div>

      {/* Info Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-blue-900 mb-1">
            Speaker Visibility
          </h3>
          <p className="text-sm text-blue-800">
            Only speakers with <span className="font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded">Confirmed</span> status will appear on the public website. 
            Speakers marked as <span className="font-semibold bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Draft</span> remain hidden and are only visible in this admin panel.
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm text-slate-900">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3">Photo</th>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {speakers.map(s => (
              <tr key={s.id} className="border-t">
                <td className="p-3">
                  {s.photo_url ? (
                    <img
                      src={s.photo_url}
                      alt=""
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    "—"
                  )}
                </td>

                <td className="p-3">{s.full_name}</td>
                <td className="p-3">{s.speaking_type}</td>

                <td className="p-3 capitalize">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      s.status === "confirmed"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {s.status}
                  </span>
                </td>

<td className="p-3">
  <div className="flex justify-end items-center gap-2">
    <button
      onClick={() => moveSpeaker(s, "up")}
      className="text-gray-600"
    >
      <ArrowUp size={16} />
    </button>

    <button
      onClick={() => moveSpeaker(s, "down")}
      className="text-gray-600"
    >
      <ArrowDown size={16} />
    </button>

    <button
      onClick={() => openEdit(s)}
      className="text-amber-600"
    >
      <Pencil size={16} />
    </button>

    <button
      onClick={() => handleDelete(s)}
      className="text-red-600"
    >
      <Trash2 size={16} />
    </button>
  </div>
</td>



              </tr>
            ))}

            {speakers.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-500">
                  No speakers added yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ================= MODAL ================= */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header - Fixed */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-white sticky top-0">
              <h2 className="text-xl font-semibold text-slate-900">
                {editing ? "Edit Speaker" : "Add Speaker"}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Form - Scrollable */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
                {/* Row 1: Full Name & Organization */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="w-full border rounded-md px-3 py-2 text-slate-900 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization
                    </label>
                    <input
                      name="organization"
                      value={form.organization || ""}
                      onChange={handleChange}
                      placeholder="Company/Institution"
                      className="w-full border rounded-md px-3 py-2 text-slate-900 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Row 2: Speaking Topic */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Speaking Topic
                  </label>
                  <input
                    name="speaking_topic"
                    value={form.speaking_topic || ""}
                    onChange={handleChange}
                    placeholder="Topic or session title"
                    className="w-full border rounded-md px-3 py-2 text-slate-900 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                {/* Row 3: Speaking Type & Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Speaking Type
                    </label>
                    <select
                      name="speaking_type"
                      value={form.speaking_type}
                      onChange={handleChange}
                      className="w-full border rounded-md px-3 py-2 text-slate-900 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      <option value="Keynote">Keynote</option>
                      <option value="Panelist">Panelist</option>
                      <option value="Moderator">Moderator</option>
                      <option value="Workshop">Workshop</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      className="w-full border rounded-md px-3 py-2 text-slate-900 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                      <option value="draft">Draft (hidden)</option>
                      <option value="confirmed">Confirmed (public)</option>
                    </select>
                  </div>
                </div>

                {/* Row 4: Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biography
                  </label>
                  <textarea
                    name="bio"
                    value={form.bio || ""}
                    onChange={handleChange}
                    placeholder="Speaker bio and background..."
                    rows={4}
                    className="w-full border rounded-md px-3 py-2 text-slate-900 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>

                {/* Row 5: Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profile Photo
                  </label>
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          const file = e.target.files[0]
                          if (file) {
                            setPhotoFile(file)
                            setPhotoPreview(URL.createObjectURL(file))
                          }
                        }}
                        className="w-full text-sm text-slate-900 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG or WEBP (max. 2MB)
                      </p>
                    </div>

                    {photoPreview && (
                      <img
                        src={photoPreview}
                        alt="Preview"
                        className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Footer - Sticky */}
              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold transition disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save Speaker"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
